import { OrganizationDataRecipientDto } from "../../common/service/server/dto/OrganizationDataRecipientDto";
import { alphabetically } from "../surveys/util";

// Category groupings as specified in the requirements
export const VENDOR_CATEGORY_GROUPS = {
  SALES_MARKETING: {
    name: "Sales/Marketing",
    order: 1,
    categories: [
      "Ad Network",
      "Sales & Marketing Tool",
      "Data Analytics Provider",
      "Data Broker",
      "Data Cooperative",
    ],
  },
  CUSTOMER_PURCHASES: {
    name: "Customer Purchases",
    order: 2,
    categories: ["Commerce Software Tool", "Payment Processor", "Shipping Service"],
  },
  CUSTOMER_SUPPORT: {
    name: "Customer Support",
    order: 3,
    categories: ["Customer Support Tool"],
  },
  BUSINESS_OPERATIONS: {
    name: "Business Operations",
    order: 4,
    categories: [
      "Business Operations Tool",
      "Cloud Computing & Storage Provider",
      "Collaboration & Productivity Tool",
      "Communications Tool",
      "IT Infrastructure Service",
      "Web Hosting Service",
    ],
  },
  MISCELLANEOUS: {
    name: "Miscellaneous/Other",
    order: 5,
    categories: [], // This will catch all uncategorized vendors
  },
};

/**
 * Maps a vendor category to its category group
 */
export const getCategoryGroup = (vendorCategory: string | null | undefined): string => {
  if (!vendorCategory) {
    return VENDOR_CATEGORY_GROUPS.MISCELLANEOUS.name;
  }

  // Check each category group for a match
  for (const group of Object.values(VENDOR_CATEGORY_GROUPS)) {
    if (group.categories.includes(vendorCategory)) {
      return group.name;
    }
  }

  // If no match found, put in miscellaneous
  return VENDOR_CATEGORY_GROUPS.MISCELLANEOUS.name;
};

/**
 * Gets the sort order for a category group
 */
export const getCategoryGroupOrder = (groupName: string): number => {
  const group = Object.values(VENDOR_CATEGORY_GROUPS).find((g) => g.name === groupName);
  return group?.order ?? 999;
};

/**
 * Groups vendors by category and sorts them appropriately
 */
export const groupAndSortVendorsByCategory = (
  vendors: OrganizationDataRecipientDto[],
): {
  [categoryGroup: string]: OrganizationDataRecipientDto[];
} => {
  if (!vendors) {
    return {};
  }

  // Group vendors by category group
  const groupedVendors: { [categoryGroup: string]: OrganizationDataRecipientDto[] } = {};

  vendors.forEach((vendor) => {
    const categoryGroup = getCategoryGroup(vendor.category);
    if (!groupedVendors[categoryGroup]) {
      groupedVendors[categoryGroup] = [];
    }
    groupedVendors[categoryGroup].push(vendor);
  });

  // Sort vendors within each group alphabetically
  Object.keys(groupedVendors).forEach((group) => {
    groupedVendors[group].sort(alphabetically);
  });

  return groupedVendors;
};

/**
 * Sorts vendors by category groups, then alphabetically within each group
 * Returns a flat array of vendors sorted by category group order
 */
export const sortVendorsByCategory = (
  vendors: OrganizationDataRecipientDto[],
): OrganizationDataRecipientDto[] => {
  if (!vendors) {
    return [];
  }

  const groupedVendors = groupAndSortVendorsByCategory(vendors);
  const sortedGroupNames = Object.keys(groupedVendors).sort((a, b) => {
    return getCategoryGroupOrder(a) - getCategoryGroupOrder(b);
  });

  // Flatten the grouped vendors back into a single array
  const result: OrganizationDataRecipientDto[] = [];
  sortedGroupNames.forEach((groupName) => {
    result.push(...groupedVendors[groupName]);
  });

  return result;
};
